import { useEffect, useId, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import crossIcon from '@/assets/icons/cross.svg';
import './Modal.scss';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

export function Modal({ isOpen, onClose, children, title }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDialogElement>) => {
    // A click on ::backdrop is dispatched on the dialog itself, so compare against its box.
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const outside = e.clientX < rect.left || e.clientX > rect.right
      || e.clientY < rect.top || e.clientY > rect.bottom;
    if (outside) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (!isOpen) {
      if (dialog.open) dialog.close();
      return;
    }

    if (!dialog.open) dialog.showModal();
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Portals are unsupported by the server renderer.
  if (typeof document === 'undefined') return null;

  return createPortal(
    // Backdrop click is a mouse-only shortcut: the native dialog already closes on Escape through onClose.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby={title ? titleId : undefined}
      onClose={onClose}
      onClick={handleBackdropClick}
    >
      <button
        type="button"
        className="modal__close"
        onClick={onClose}
        aria-label="Fermer"
      >
        <img src={crossIcon} alt="" />
      </button>
      {title && <h2 id={titleId} className="modal__title">{title}</h2>}
      <div className="modal__content">
        {children}
      </div>
    </dialog>,
    document.body
  );
}

export default Modal;
